/**
 * Checksum computation utilities for file uploads
 * Uses Web Crypto API for SHA256 and SHA1 checksums
 */

// Mapping of checksum algorithms to S3 header names
const CHECKSUM_HEADER_BY_ALGO = {
    SHA1: 'x-amz-checksum-sha1',
    SHA256: 'x-amz-checksum-sha256',
    CRC32: 'x-amz-checksum-crc32',
    CRC32C: 'x-amz-checksum-crc32c',
    CRC64NVME: 'x-amz-checksum-crc64nvme'
};

// Mapping of algorithm names to Web Crypto API algorithm identifiers
const WEB_CRYPTO_ALGO_BY_NAME = {
    SHA1: 'SHA-1',
    SHA256: 'SHA-256'
};

/**
 * Normalize checksum algorithm name to uppercase
 * @param {string} value - Algorithm name from backend
 * @returns {string|null} Normalized algorithm name or null
 */
export function normalizeChecksumAlgorithm(value) {
    return (value || '').trim().toUpperCase() || null;
}

/**
 * Get the S3 checksum header name for a given algorithm
 * @param {string} algorithm - Algorithm name (e.g., "SHA256", "sha256")
 * @returns {string|null} S3 header name or null if not supported
 */
export function getChecksumHeaderName(algorithm) {
    const normalized = normalizeChecksumAlgorithm(algorithm);
    return normalized ? CHECKSUM_HEADER_BY_ALGO[normalized] : null;
}

/**
 * Convert ArrayBuffer to base64 string
 * @param {ArrayBuffer} buffer - Binary data
 * @returns {string} Base64-encoded string
 */
function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    // Process in chunks to avoid call stack overflow on large buffers
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(binary);
}

/**
 * Compute digest using Web Crypto API
 * @param {ArrayBuffer} arrayBuffer - Data to hash
 * @param {string} normalizedAlgorithm - Normalized algorithm name (e.g., "SHA256")
 * @returns {Promise<ArrayBuffer>} Hash digest
 * @throws {Error} If algorithm is not supported
 */
async function digestArrayBuffer(arrayBuffer, normalizedAlgorithm) {
    const webAlgo = WEB_CRYPTO_ALGO_BY_NAME[normalizedAlgorithm];
    
    if (!webAlgo) {
        throw new Error(`Checksum algorithm ${normalizedAlgorithm} is not supported in this browser`);
    }
    
    if (typeof crypto === 'undefined' || !crypto.subtle) {
        throw new Error('Web Crypto API is not available in this browser');
    }
    
    return await crypto.subtle.digest(webAlgo, arrayBuffer);
}

/**
 * Calculate checksum for a single chunk
 * @param {Blob} chunk - File chunk to hash
 * @param {string} normalizedAlgorithm - Normalized algorithm name (e.g., "SHA256")
 * @returns {Promise<string>} Base64-encoded checksum
 * @throws {Error} If checksum computation fails
 */
export async function calculateChunkChecksum(chunk, normalizedAlgorithm) {
    try {
        const arrayBuffer = await chunk.arrayBuffer();
        const digestBuffer = await digestArrayBuffer(arrayBuffer, normalizedAlgorithm);
        return arrayBufferToBase64(digestBuffer);
    } catch (error) {
        console.error('Failed to calculate chunk checksum:', error);
        throw new Error(`Checksum computation failed: ${error.message}`);
    }
}

/**
 * Compute checksums for all parts of a file
 * @param {File} file - File to process
 * @param {number} chunkSize - Size of each part in bytes
 * @param {string} checksumAlgorithm - Algorithm name from backend
 * @param {Function} [onProgress] - Optional callback for progress updates (currentPart, totalParts)
 * @returns {Promise<Array<{part_number: number, checksum: string}>>} Array of part checksums
 * @throws {Error} If checksum computation fails
 */
export async function computeFilePartChecksums(file, chunkSize, checksumAlgorithm, onProgress = null) {
    const normalized = normalizeChecksumAlgorithm(checksumAlgorithm);
    if (!normalized) {
        throw new Error('Invalid checksum algorithm specified');
    }
    
    // Validate Web Crypto API support for this algorithm
    if (!WEB_CRYPTO_ALGO_BY_NAME[normalized]) {
        throw new Error(`Checksum algorithm ${normalized} is not supported. Supported algorithms: SHA256, SHA1`);
    }
    
    const totalParts = Math.ceil(file.size / chunkSize);
    const parts = [];
    
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        
        try {
            const checksum = await calculateChunkChecksum(chunk, normalized);
            parts.push({ part_number: partNumber, checksum });
            
            // Call progress callback if provided
            if (onProgress && typeof onProgress === 'function') {
                onProgress(partNumber, totalParts);
            }
        } catch (error) {
            console.error(`Failed to compute checksum for part ${partNumber}/${totalParts}:`, error);
            throw new Error(`Failed to compute checksum for part ${partNumber}: ${error.message}`);
        }
    }
    
    return parts;
}

/**
 * Check if a checksum algorithm is supported by this implementation
 * @param {string} algorithm - Algorithm name to check
 * @returns {boolean} True if supported
 */
export function isChecksumAlgorithmSupported(algorithm) {
    const normalized = normalizeChecksumAlgorithm(algorithm);
    return normalized && WEB_CRYPTO_ALGO_BY_NAME[normalized] !== undefined;
}
